// The admin run console's payloads. Plain types rather than a class: nothing here is
// edited, so there is no state to own — every screen reads and renders.

export type RunStuckReason = 'errored' | 'unobtainable' | 'unsettled' | 'lease_held' | 'no_callback';

// The words a support person reads, in the API's own REASONS order. That order is not cosmetic:
// `reason_for` returns the FIRST match, so it decides which single reason a run that matches
// several reports. Sorting these would make the filter list disagree with the row labels.
//
// Two of them are about the ENGINE (no pass has run, a pass died holding the lease) and two about
// a STEP (it failed, it never finished), which is why the step ones say "a step" and the engine
// ones say "processing". Every one of these means something is WRONG. Waiting on a person is
// deliberately not among them: it is what a referral in progress looks like, so folding it in
// makes this filter match nearly every open run and tell you nothing.
export const STUCK_REASON_LABELS: Record<RunStuckReason, string> = {
  errored: 'A step failed',
  unobtainable: 'Waiting on information nothing will supply',
  // Rare and serious, not a slow day: the flag behind it is set and cleared inside one job, so a
  // run still carrying it is one whose evaluation never completed.
  unsettled: 'Processing has stopped',
  lease_held: 'Processing stalled part-way through',
  // Says only what the predicate checks. It matches any step sitting at `running` past a
  // per-kind age ceiling, dispatched or not, so it must not claim the step was sent anywhere.
  no_callback: 'A step started and never finished',
};

export type AdminRunStep = {
  id: string;
  actionType: string;
  status: string;
  effectiveStatus: string;
  stale: boolean;
  categoryKey: string | null;
};

export type AdminRunStepDetail = AdminRunStep & {
  // What this client calls the step, from workflow lookups. Not derivable in the browser: the
  // class name `ResolveAttrValues` de-camelizes to "Resolve Attr Values" and the step is called
  // "Confirm Referral Source".
  actionLabel: string;
  current: boolean;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  // What it ran against, stamped when it consumed its inputs, and what it recorded. The pair is
  // most of why anyone opens a step: a retried step's earlier attempt usually explains the failure.
  // Pre-formatted JSON text, not an object: OliveBranch camelizes the keys of any hash in the
  // response, and these carry engine value names (`member_id`) that the rest of the console shows
  // verbatim. The API masks and formats them; this just prints them.
  inputs: string;
  outputs: string;
  unobtainableValues: string[];
  previousId: string | null;
  completedByName: string | null;
  lane: string | null;
  dispatchedRef: string | null;
};

export type AdminWorkflowRun = {
  id: string;
  // Who the referral is about, composed from the stored first/last pair. Null when neither half
  // has been written yet, which is ordinary on a run that has only just arrived.
  patientName: string | null;
  status: string;
  outcome: string;
  createdAt: string;
  concludedAt: string | null;
  dirty: boolean;
  // When the unprocessed change landed, or null on a healthy run. Not "slightly stale": it means
  // the evaluator has stopped for this run.
  dirtySince: string | null;
  archivedAt: string | null;
  clientName: string | null;
  workflowName: string;
  apiName: string;
  version: number;
  publishedVersion: number | null;
  versionsBehind: number;
  facilityNames: string[];
  stuckReason: RunStuckReason | null;
  // When the oldest step on this run started waiting on someone. Not a fault: a referral in
  // progress is normally waiting on someone.
  awaitingSince: string | null;
  steps: AdminRunStep[];
};

// Why the run is waiting, grouped by cause and ranked by the API. The browser renders these and
// adds nothing: the grouping needs the run's own composition, which only the server holds.
export type WaitingCause = {
  // Exactly the kinds the API emits, keyed by WHY rather than by the generic word "step": a
  // failure and a step that started and never finished are different investigations.
  // 'dirty' is deliberately not here. It is not something the evaluator found, it is the evaluator
  // not having looked, so it undermines this whole list rather than belonging in it. The panel
  // states it above the causes, off the run's own `dirty` / `dirtySince`.
  kind: 'errored' | 'value' | 'running' | 'person' | 'ready';
  key: string;
  // Usually one; several when values blocked on identical steps are named together. Each carries
  // the stored name as well as the label, because two values can share a label: `payer_id` and
  // `external_payer_id` are both "Payer ID".
  subjects: { name: string; label: string }[];
  lanes: string[];
  // One entry per step held up, with its facilities. A fanned-out step is one thing held up, not
  // one per facility, so the lanes hang off the step rather than repeating it.
  blocking: { type: string; label: string; lanes: string[] }[];
};

export type AdminWorkflowRunDetail = Omit<AdminWorkflowRun, 'steps'> & {
  // The run's own categories in declared order, for grouping the steps table.
  categories: { key: string; label: string }[];
  waiting: WaitingCause[];
  createdByName: string | null;
  externalIds: { kind: string; value: string }[];
  facets: {
    id: string;
    dimensionKey: string;
    identityKey: string | null;
    label: string | null;
    position: number;
    source: string;
    archivedAt: string | null;
  }[];
  steps: AdminRunStepDetail[];
};

export type AdminRunValue = {
  id: string;
  name: string;
  nameLabel: string;
  value: unknown;
  source: string;
  current: boolean;
  createdAt: string;
  writtenByName: string | null;
  writtenByStep: string | null;
  writtenByStepLabel: string | null;
  // Which run of that step wrote it: a fanned-out step writes one value per coverage or facility.
  writtenByScope: string | null;
  scope: string | null;
  // The scope's identity, which is what the history groups on. Two coverages can share a payer
  // name, and so can a removed one and its replacement, so the label merges writes belonging to
  // different things.
  scopeId: string | null;
  // The member this value belongs to has been archived, which is why none of its writes are
  // current — as opposed to a newer write having replaced them.
  scopeArchived: boolean;
};

export type AdminRunLog = {
  id: string;
  event: string;
  summary: string;
  detail: string | null;
  sequence: number;
  createdAt: string;
  actorKind: string;
  actorName: string | null;
  scope: string | null;
};

export type AdminRunHealth = {
  byStep: {
    actionType: string;
    actionLabel: string;
    started: number;
    errored: number;
    running: number;
    actionRequired: number;
    flagged: number;
  }[];
  engine: { unsettled: number; leaseHeld: number; behindVersion: number };
  byClient: { client: string; open: number }[];
};
